import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TableCell,
  TextField,
  Tooltip,
  Typography,
  Divider,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import { useSnackbar } from "../../../components/snackbar/SnackbarProvider";
import {
  createAdminKitType,
  fetchAdminKitTypes,
  fetchAdminSampleTypes,
  fetchStudies,
  updateAdminKitType,
} from "../../../utils/api";

const maroon = "#500000";

const EMPTY_DRAFT = {
  kitType: "",
  expectedSampleTypes: [],
  studyIds: [],
  comment: "",
};

const getStudyShortName = (study) => {
  if (!study) {
    return "";
  }

  return study.studyShortName || "";
};

const KitTypeManagementCard = () => {
  const [rows, setRows] = useState([]);
  const [studies, setStudies] = useState([]);
  const [sampleTypes, setSampleTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState("");
  const [formError, setFormError] = useState("");
  const { openSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  const sampleTypeMap = useMemo(() => {
    const map = new Map();
    sampleTypes.forEach((sampleType) => {
      map.set(sampleType.sampleLetter, sampleType);
    });
    return map;
  }, [sampleTypes]);

  const studyMap = useMemo(() => {
    const map = new Map();
    studies.forEach((study) => {
      map.set(study.irbStudy, study);
    });
    return map;
  }, [studies]);

  const loadData = async () => {
    try {
      setLoading(true);
      setPageError("");
      const [kitTypeResponse, studyResponse, sampleTypeResponse] = await Promise.all([
        fetchAdminKitTypes(),
        fetchStudies(),
        fetchAdminSampleTypes(),
      ]);
      setRows(Array.isArray(kitTypeResponse) ? kitTypeResponse : []);
      setStudies(Array.isArray(studyResponse) ? studyResponse : []);
      setSampleTypes(Array.isArray(sampleTypeResponse) ? sampleTypeResponse : []);
    } catch (error) {
      console.error("Error loading kit type management data:", error);
      const msg = error?.response?.data?.message || error?.message || "Failed to load kit types.";
      setPageError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.kitType);
    setDraft({
      kitType: row.kitType || "",
      expectedSampleTypes: row.expectedSampleTypes || [],
      studyIds: (row.usedByStudies || []).map((study) => study.irbStudy),
      comment: row.comment || "",
    });
    setFormError("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!draft.kitType.trim()) {
      setFormError("Kit type is required.");
      return;
    }
    if (draft.expectedSampleTypes.length === 0) {
      setFormError("Select at least one sample type.");
      return;
    }
    if (draft.studyIds.length === 0) {
      setFormError("Select at least one study.");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        kitType: draft.kitType.trim().toUpperCase(),
        expectedSampleTypes: draft.expectedSampleTypes,
        studyIds: draft.studyIds,
        comment: draft.comment.trim() || null,
      };

      if (editingId) {
        await updateAdminKitType(editingId, payload);
        openSnackbar("Kit type updated successfully!", "success");
      } else {
        await createAdminKitType(payload);
        openSnackbar("Kit type created successfully!", "success");
      }

      setDialogOpen(false);
      setDraft(EMPTY_DRAFT);
      await loadData();
    } catch (error) {
      console.error("Error saving kit type:", error);
      const msg = error?.response?.data?.message || error?.message || "Failed to save kit type.";
      setFormError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const renderSampleTypeLabel = (code) => {
    const sampleType = sampleTypeMap.get(code);
    if (!sampleType) {
      return code;
    }
    return `${sampleType.sampleLetter} - ${sampleType.shortName || sampleType.longName || sampleType.sampleLetter}`;
  };

  const renderStudyTooltip = (studyId) => {
    const study = studyMap.get(studyId);
    if (!study) {
      return studyId;
    }

    return study.studyName || study.irbStudy;
  };

  const renderStudyLabel = (studyId) => {
    const study = studyMap.get(studyId);
    if (!study) {
      return studyId;
    }

    return getStudyShortName(study) || study.irbStudy;
  };

  const renderSampleTypeTooltip = (code) => {
    const sampleType = sampleTypeMap.get(code);
    if (!sampleType) {
      return code;
    }

    return sampleType.shortName || sampleType.longName || sampleType.sampleLetter;
  };

  return (
    <Card elevation={1} sx={{ boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25)", borderRadius: 0 }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: maroon }}>
              Kit Type Management
            </Typography>
          </Box>
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{
              color: "#666",
              fontWeight: 600,
              fontSize: "0.75rem",
              py: 0.5,
              px: 1.5,
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)", color: "#333" },
            }}
            data-tour-id="tour-add-kit-type"
          >
            ADD KIT TYPE
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {pageError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {pageError}
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "220px minmax(0, 1fr)" },
            gap: 3,
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 2,
              minHeight: 84,
              textAlign: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: maroon, fontWeight: 700, fontSize: "0.8rem" }}
            >
              TOTAL KIT TYPES
            </Typography>
            <Typography variant="h3" sx={{ color: maroon, fontWeight: 700 }}>
              {rows.length}
            </Typography>
          </Box>

          <Box sx={{ minWidth: 0, pr: { xs: 0, md: 4 } }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "120px minmax(0, 1fr) minmax(0, 1fr) 96px",
                gap: 2,
                pb: 1,
              }}
            >
              {["TYPE", "STUDIES", "SAMPLE TYPES", "ACTION"].map((header, index) => (
                <Typography
                  key={header}
                  variant="caption"
                  sx={{
                    color: "#666",
                    fontWeight: 700,
                    textAlign: index === 3 ? "right" : "left",
                  }}
                >
                  {header}
                </Typography>
              ))}
            </Box>

            {loading ? (
              <Box sx={{ py: 5, textAlign: "center", color: "#666" }}>
                Loading kit types...
              </Box>
            ) : rows.length === 0 ? (
              <Box sx={{ py: 5, textAlign: "center", color: "#666" }}>
                No kit types found.
              </Box>
            ) : (
              <List sx={{ py: 0 }}>
                {rows.map((row, index) => (
                  <ListItem
                    key={row.kitType}
                    sx={{
                      px: 0,
                      py: 0.75,
                      display: "grid",
                      gridTemplateColumns: "120px minmax(0, 1fr) minmax(0, 1fr) 96px",
                      gap: 2,
                      borderTop: index === 0 ? "1px solid #eee" : 0,
                      borderBottom: index === rows.length - 1 ? 0 : "1px solid #eee",
                      alignItems: "center",
                    }}
                  >
                    <ListItemText
                      primary={row.kitType}
                      primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: 500 }}
                      sx={{ m: 0 }}
                    />
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
                      {(row.usedByStudies || []).map((study) => (
                        <Tooltip
                          key={`${row.kitType}-${study.irbStudy}`}
                          title={renderStudyTooltip(study.irbStudy)}
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            label={getStudyShortName(study) || "—"}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
                      {(row.expectedSampleTypes || []).map((sampleType) => (
                        <Tooltip
                          key={`${row.kitType}-${sampleType}`}
                          title={renderSampleTypeTooltip(sampleType)}
                        >
                          <Chip size="small" label={sampleType} />
                        </Tooltip>
                      ))}
                    </Box>
                    <Box sx={{ justifySelf: "end" }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => openEdit(row)}
                          sx={{ color: maroon }}
                          data-tour-id={index === 0 ? "tour-edit-kit-type" : undefined}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: maroon, color: "white" }}>
          {editingId ? "Edit Kit Type" : "Create New Kit Type"}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              label="Kit Type"
              value={draft.kitType}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, kitType: event.target.value.toUpperCase() }))
              }
              disabled={saving || !!editingId}
              fullWidth
              required
            />

            <FormControl fullWidth>
              <InputLabel>Sample Types</InputLabel>
              <Select
                multiple
                value={draft.expectedSampleTypes}
                label="Sample Types"
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, expectedSampleTypes: event.target.value }))
                }
                renderValue={(selected) =>
                  selected.map((value) => renderSampleTypeLabel(value)).join(", ")
                }
              >
                {sampleTypes.map((sampleType) => (
                  <MenuItem key={sampleType.sampleLetter} value={sampleType.sampleLetter}>
                    {renderSampleTypeLabel(sampleType.sampleLetter)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Studies</InputLabel>
              <Select
                multiple
                value={draft.studyIds}
                label="Studies"
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, studyIds: event.target.value }))
                }
                renderValue={(selected) =>
                  selected.map((value) => renderStudyLabel(value)).join(", ")
                }
              >
                {studies.map((study) => (
                  <MenuItem key={study.irbStudy} value={study.irbStudy}>
                    {renderStudyLabel(study.irbStudy)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Comment"
              value={draft.comment}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, comment: event.target.value }))
              }
              fullWidth
              multiline
              minRows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => { setDialogOpen(false); setFormError(""); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ backgroundColor: maroon, "&:hover": { backgroundColor: "#4a0000" } }}
          >
            {saving ? "Saving..." : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default KitTypeManagementCard;
